import type { AxiosInstance, AxiosResponse } from 'axios';
import type { RootResponse } from '@/models/rootResponse';
import type { Trophy } from '@/models/trophy';

const TROPHIES_FETCH_LIMIT = 50;

export type MutateTrophy = {
   id?: string;
   name: string;
   surveyId: string;
   selectedChoiceIds: string[];
   originalChoiceIds?: string[];
};

type SaveTrophiesParams = {
   toCreate: MutateTrophy[];
   toUpdate: (MutateTrophy & { id: string })[];
};

type TrophyRelationOperation = {
   operation: 'attach' | 'detach';
   key: string;
};

type TrophyMutateOperation = {
   operation: 'create' | 'update';
   key?: string;
   attributes: { name: string };
   relations: {
      surveys?: TrophyRelationOperation[];
      questionChoices: TrophyRelationOperation[];
   };
};

function buildSurveyRelation(surveyId: string): TrophyRelationOperation {
   return { operation: 'attach', key: surveyId };
}

function buildChoiceRelationOperations(
   choiceIds: string[],
   operation: 'attach' | 'detach',
): TrophyRelationOperation[] {
   return choiceIds.map((id) => ({ operation, key: id }));
}

function computeChoiceRelationChanges(
   originalChoiceIds: string[],
   selectedChoiceIds: string[],
): { choicesToDetach: string[]; choicesToAttach: string[] } {
   const previousChoiceIds = new Set(originalChoiceIds);
   const nextChoiceIds = new Set(selectedChoiceIds);
   const choicesToDetach = originalChoiceIds.filter(
      (id) => !nextChoiceIds.has(id),
   );
   const choicesToAttach = selectedChoiceIds.filter(
      (id) => !previousChoiceIds.has(id),
   );
   return { choicesToDetach, choicesToAttach };
}

function buildCreateTrophyOperation(
   trophy: MutateTrophy,
): TrophyMutateOperation {
   return {
      operation: 'create',
      attributes: { name: trophy.name },
      relations: {
         surveys: [buildSurveyRelation(trophy.surveyId)],
         questionChoices: buildChoiceRelationOperations(
            trophy.selectedChoiceIds,
            'attach',
         ),
      },
   };
}

function buildUpdateTrophyOperation(
   trophy: MutateTrophy & { id: string },
): TrophyMutateOperation {
   const { choicesToDetach, choicesToAttach } = computeChoiceRelationChanges(
      trophy.originalChoiceIds ?? [],
      trophy.selectedChoiceIds,
   );

   return {
      operation: 'update',
      key: trophy.id,
      attributes: { name: trophy.name },
      relations: {
         questionChoices: [
            ...buildChoiceRelationOperations(choicesToDetach, 'detach'),
            ...buildChoiceRelationOperations(choicesToAttach, 'attach'),
         ],
      },
   };
}

class TrophyService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async searchTrophies(surveyId: string): Promise<Trophy[]> {
      const response: AxiosResponse<RootResponse<Trophy[]>> =
         await this.provider.post('rest/trophies/search', {
            search: {
               filters: [
                  { field: 'surveys.id', operator: '=', value: surveyId },
               ],
               includes: [{ relation: 'questionChoices' }],
               limit: TROPHIES_FETCH_LIMIT,
            },
         });

      return response.data.data;
   }

   async saveTrophies({
      toCreate,
      toUpdate,
   }: SaveTrophiesParams): Promise<void> {
      const mutateOperations: TrophyMutateOperation[] = [
         ...toCreate.map(buildCreateTrophyOperation),
         ...toUpdate.map(buildUpdateTrophyOperation),
      ];

      if (mutateOperations.length === 0) return;

      await this.provider
         .post('rest/trophies/mutate', { mutate: mutateOperations })
         .then(() => undefined);
   }

   async deleteTrophy(trophyId: string): Promise<void> {
      await this.provider.delete('rest/trophies', {
         data: { resources: [trophyId] },
      });
   }
}

export default TrophyService;
